import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Modal, Button, InputNumber, Input, DatePicker, Upload, message } from 'antd';
import {
	PlusOutlined,
	FileTextOutlined,
	DownOutlined,
	UpOutlined,
	DeleteOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import moment from 'moment';
import { getSignedUrl } from 'store/commonSlice';

const ENTITY = 'client_invoice';
const IMAGE_EXT = ['jpg', 'jpeg', 'png', 'gif', 'webp'];

const StyledModal = styled(Modal)`
	.ant-modal-title {
		font-size: 20px;
		font-weight: 700;
		color: #1e293b;
	}
	.ant-modal-body {
		padding: 24px;
	}
`;

const Field = styled.div`
	margin-bottom: 18px;
`;

const FieldLabel = styled.div`
	font-size: 14px;
	font-weight: 600;
	color: #1f2937;
	margin-bottom: 6px;
`;

const Optional = styled.span`
	color: #9ca3af;
	font-weight: 400;
	font-size: 12px;
	margin-left: 4px;
`;

const Helper = styled.div`
	font-size: 12px;
	color: #4f6ef7;
	margin-top: 6px;
`;

const Footer = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 12px;
	margin-top: 24px;
	border-top: 1px solid #e5e7eb;
	padding-top: 16px;
`;

const PrimaryButton = styled(Button)`
	height: 40px;
	padding: 0 32px;
	background-color: #1e3a5f;
	border-color: #1e3a5f;
	color: #fff;
	border-radius: 6px;
	&:hover,
	&:focus {
		background-color: #18405d !important;
		border-color: #18405d !important;
		color: #fff !important;
	}
`;

const ProgressSection = styled.div`
	margin-bottom: 20px;
	padding-bottom: 20px;
	border-bottom: 1px solid #e5e7eb;
`;

const ProgressHead = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 8px;
`;

const ProgressTrack = styled.div`
	background: #eef2f6;
	border-radius: 4px;
	height: 8px;
	overflow: hidden;
`;

const ProgressFill = styled.div`
	height: 100%;
	background: #4aa3df;
	width: ${(props) => props.pct || 0}%;
`;

const AmountsRow = styled.div`
	display: flex;
	justify-content: space-between;
	margin-top: 14px;
`;

const AmountItem = styled.div`
	text-align: ${(props) => props.align || 'left'};
`;

const AmountLabel = styled.div`
	font-size: 11px;
	font-weight: 600;
	color: #94a3b8;
	text-transform: uppercase;
	letter-spacing: 0.3px;
`;

const AmountValue = styled.div`
	font-size: 15px;
	font-weight: 700;
	color: ${(props) => props.color || '#1f2937'};
	margin-top: 2px;
`;

// ── Payment history ──
const HistoryCard = styled.div`
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	margin-bottom: 20px;
`;

const HistoryHead = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 14px 16px;
	cursor: pointer;
	font-weight: 600;
	color: #1f2937;
`;

const HistoryRow = styled.div`
	padding: 0 16px 14px;
`;

const HistoryTop = styled.div`
	display: flex;
	justify-content: space-between;
	font-weight: 700;
	color: #1f2937;
`;

const HistorySub = styled.div`
	display: flex;
	justify-content: space-between;
	font-size: 12px;
	color: #94a3b8;
	margin: 2px 0 8px;
`;

// ── Image tiles ──
const Tiles = styled.div`
	display: flex;
	gap: 12px;
	flex-wrap: wrap;
`;

const Tile = styled.div`
	position: relative;
	width: 96px;
	height: 96px;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	cursor: pointer;
	background: #fafafa;

	&:hover > .remove-tile {
		opacity: 1;
	}
`;

const RemoveBtn = styled.button`
	position: absolute;
	top: 4px;
	right: 4px;
	width: 22px;
	height: 22px;
	border: none;
	border-radius: 50%;
	background: rgba(0, 0, 0, 0.6);
	color: #fff;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	opacity: 0;
	transition: opacity 0.15s ease;
	font-size: 12px;
	padding: 0;
`;

const AddTile = styled.div`
	width: 96px;
	height: 96px;
	border: 1px dashed #d1d5db;
	border-radius: 8px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	color: #94a3b8;
	cursor: pointer;
	font-size: 12px;
`;

const Thumb = styled.img`
	width: 100%;
	height: 64px;
	object-fit: cover;
`;

const TileName = styled.div`
	font-size: 11px;
	color: #1890ff;
	padding: 4px 6px;
	max-width: 96px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const FileChip = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	background: #f0f5ff;
	border: 1px solid #d6e4ff;
	color: #1890ff;
	border-radius: 4px;
	padding: 3px 8px;
	font-size: 12px;
	cursor: pointer;
`;

const formatCurrency = (value) => {
	if (value == null || value === '') return '-';
	const num = Number(value);
	return Number.isNaN(num) ? value : `₹ ${num.toLocaleString('en-IN')}`;
};

const fileNameFromKey = (key = '') => {
	const match = String(key).match(
		/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}-/i
	);
	return match ? key.slice(match[0].length) : key;
};
const isImageName = (name = '') => IMAGE_EXT.includes(name.split('.').pop().toLowerCase());

const EMPTY = {
	paid_amount: undefined,
	utr_number: '',
	paid_date: null,
	tds_amount: undefined,
	customer_debit: undefined,
};

function MarkPaymentModal({ visible, invoice, payments, saving, onSave, onClose }) {
	const dispatch = useDispatch();
	const [form, setForm] = useState(EMPTY);
	// Each entry: { key, name, url } (url = local object URL for instant preview).
	const [proofFiles, setProofFiles] = useState([]);
	const [debitProof, setDebitProof] = useState(null);
	const [uploading, setUploading] = useState(false);
	const [historyOpen, setHistoryOpen] = useState(true);

	useEffect(() => {
		if (visible) {
			setForm(EMPTY);
			setProofFiles([]);
			setDebitProof(null);
			setHistoryOpen(true);
		}
	}, [visible]);

	const grandTotal = Number(invoice?.grand_total) || 0;
	const paid = Number(invoice?.paid) || 0;
	const pending = invoice?.pending != null ? Number(invoice.pending) : grandTotal - paid;
	const paidPct = grandTotal > 0 ? Math.round((paid / grandTotal) * 100) : 0;

	const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

	const uploadFile = async (file) => {
		setUploading(true);
		try {
			const res = await dispatch(
				getSignedUrl({ url_type: 'upload', data: { entity: ENTITY, keys: [file.name] } })
			);
			const fileData = res?.data?.[0];
			if (!fileData?.url || !fileData?.filename) throw new Error('No signed URL');
			await fetch(fileData.url, {
				method: 'PUT',
				body: file,
				headers: { 'Content-Type': file.type },
			});
			return fileData.filename;
		} catch (error) {
			message.error('Failed to upload file.');
			return null;
		} finally {
			setUploading(false);
		}
	};

	const handlePaymentProof = async (file) => {
		const url = URL.createObjectURL(file);
		const key = await uploadFile(file);
		if (key) setProofFiles((prev) => [...prev, { key, name: file.name, url }]);
		return Upload.LIST_IGNORE;
	};

	const handleDebitProof = async (file) => {
		const url = URL.createObjectURL(file);
		const key = await uploadFile(file);
		if (key) setDebitProof({ key, name: file.name, url });
		return Upload.LIST_IGNORE;
	};
	const viewKey = async (key) => {
		const res = await dispatch(
			getSignedUrl({ url_type: 'view', data: { entity: ENTITY, keys: [key] } })
		);
		const url = res?.data?.[0]?.url;
		if (url) window.open(url);
		else message.error('Could not open the file.');
	};

	const handleSave = () => {
		const amt = Number(form.paid_amount);
		if (!Number.isFinite(amt) || amt <= 0) {
			message.error('Payment amount must be a number greater than 0.');
			return;
		}
		if (amt > pending + 0.01) {
			message.error(`Payment amount cannot exceed the remaining ${formatCurrency(pending)}.`);
			return;
		}
		const utr = String(form.utr_number || '').trim();
		if (!/^\d+$/.test(utr) || Number(utr) <= 0) {
			message.error('UTR number must be a positive number (digits only).');
			return;
		}
		if (Number(form.tds_amount) < 0 || Number(form.customer_debit) < 0) {
			message.error('TDS / Customer Debit cannot be negative.');
			return;
		}
		onSave({
			client_invoice_id: invoice?.id,
			paid_amount: amt,
			utr_number: utr,
			paid_date: form.paid_date ? moment(form.paid_date).format('YYYY-MM-DD') : undefined,
			tds_amount: form.tds_amount || 0,
			customer_debit: form.customer_debit || 0,
			image_keys: proofFiles.map((f) => f.key),
			customer_debit_proof_key: debitProof?.key || null,
		});
	};

	const renderTile = (file, onRemove) => (
		<Tile key={file.key} onClick={() => (file.url ? window.open(file.url) : viewKey(file.key))}>
			{onRemove && (
				<RemoveBtn
					type="button"
					className="remove-tile"
					title="Remove image"
					onClick={(e) => {
						e.stopPropagation();
						onRemove();
					}}
				>
					<DeleteOutlined />
				</RemoveBtn>
			)}
			{file.url && isImageName(file.name) ? (
				<Thumb src={file.url} alt={file.name} />
			) : (
				<FileTextOutlined style={{ fontSize: 26, color: '#1890ff' }} />
			)}
			<TileName>{file.name}</TileName>
		</Tile>
	);

	return (
		<StyledModal
			title={`Mark Payment${invoice?.invoice_no ? ` - ${invoice.invoice_no}` : ''}`}
			open={visible}
			onCancel={onClose}
			footer={null}
			width={620}
		>
			<ProgressSection>
				<ProgressHead>
					<span style={{ fontSize: 13, color: '#64748b' }}>Payment Progress</span>
					<span style={{ fontSize: 14, fontWeight: 700 }}>{paidPct}% Paid</span>
				</ProgressHead>
				<ProgressTrack>
					<ProgressFill pct={paidPct} />
				</ProgressTrack>
				<AmountsRow>
					<AmountItem>
						<AmountLabel>Total Amount</AmountLabel>
						<AmountValue>{formatCurrency(grandTotal)}</AmountValue>
					</AmountItem>
					<AmountItem align="center">
						<AmountLabel>Paid</AmountLabel>
						<AmountValue color="#16a34a">{formatCurrency(paid)}</AmountValue>
					</AmountItem>
					<AmountItem align="right">
						<AmountLabel>Remaining</AmountLabel>
						<AmountValue color="#ff9800">{formatCurrency(pending)}</AmountValue>
					</AmountItem>
				</AmountsRow>
			</ProgressSection>

			{payments && payments.length > 0 && (
				<HistoryCard>
					<HistoryHead onClick={() => setHistoryOpen((o) => !o)}>
						Payment History {historyOpen ? <UpOutlined /> : <DownOutlined />}
					</HistoryHead>
					{historyOpen &&
						payments.map((p, i) => {
							const amount = Number(p.amount ?? p.paid_amount) || 0;
							const pct =
								grandTotal > 0 ? Math.round((amount / grandTotal) * 100) : 0;
							const keys = Array.isArray(p.image_keys) ? p.image_keys : [];
							return (
								// eslint-disable-next-line react/no-array-index-key
								<HistoryRow key={`${p.utr_number}-${i}`}>
									<HistoryTop>
										<span>{formatCurrency(amount)}</span>
										<span>{pct}%</span>
									</HistoryTop>
									<HistorySub>
										<span>UTR: {p.utr_number || '-'}</span>
										<span>
											{p.paid_date || p.paid_at
												? moment(p.paid_date || p.paid_at).format(
														'DD-MM-YYYY'
												  )
												: '-'}
										</span>
									</HistorySub>
									{keys.map((k) => (
										<FileChip
											key={k}
											onClick={() => viewKey(k)}
											style={{ marginRight: 6 }}
										>
											<FileTextOutlined />
											{fileNameFromKey(k)}
										</FileChip>
									))}
								</HistoryRow>
							);
						})}
				</HistoryCard>
			)}

			<Field>
				<FieldLabel>Payment Date</FieldLabel>
				<DatePicker
					style={{ width: '100%' }}
					format="DD-MM-YYYY"
					placeholder="Select date"
					value={form.paid_date}
					onChange={(d) => setField('paid_date', d)}
				/>
			</Field>

			<Field>
				<FieldLabel>UTR / Ref Number</FieldLabel>
				<Input
					value={form.utr_number}
					placeholder="Bank Transaction ID"
					onChange={(e) => setField('utr_number', e.target.value.replace(/\D/g, ''))}
					onKeyDown={(e) => {
						if (['-', '+', 'e', 'E', '.'].includes(e.key)) e.preventDefault();
					}}
				/>
			</Field>

			<Field>
				<FieldLabel>Payment Amount</FieldLabel>
				<InputNumber
					style={{ width: '100%' }}
					min={0}
					prefix="₹"
					controls={false}
					value={form.paid_amount}
					onChange={(v) => setField('paid_amount', v)}
					onKeyDown={(e) => {
						if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault();
					}}
					placeholder="Enter payment amount"
				/>
			</Field>

			<Field>
				<FieldLabel>
					TDS Deducted Amount (₹)<Optional>(optional)</Optional>
				</FieldLabel>
				<InputNumber
					style={{ width: '100%' }}
					min={0}
					controls={false}
					value={form.tds_amount}
					onChange={(v) => setField('tds_amount', v)}
					onKeyDown={(e) => {
						if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault();
					}}
					placeholder="Bank Transaction ID"
				/>
				<Helper>
					Amount deducted by client as TDS (e.g. Invoice ₹1,000 paid ₹900 → TDS = ₹100)
				</Helper>
			</Field>

			<Field>
				<FieldLabel>
					Customer Debit Amount (₹)<Optional>(optional)</Optional>
				</FieldLabel>
				<InputNumber
					style={{ width: '100%' }}
					min={0}
					controls={false}
					value={form.customer_debit}
					onChange={(v) => setField('customer_debit', v)}
					onKeyDown={(e) => {
						if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault();
					}}
					placeholder="Bank Transaction ID"
				/>
			</Field>

			<Field>
				<FieldLabel>
					Customer Debit Proof<Optional>(optional)</Optional>
				</FieldLabel>
				<Tiles>
					{debitProof && renderTile(debitProof, () => setDebitProof(null))}
					{!debitProof && (
						<Upload beforeUpload={handleDebitProof} showUploadList={false} maxCount={1}>
							<AddTile>
								<PlusOutlined />
								Add Image
							</AddTile>
						</Upload>
					)}
				</Tiles>
			</Field>

			<Field>
				<FieldLabel>Payment Proof (Screenshots)</FieldLabel>
				<Tiles>
					{proofFiles.map((f) =>
						renderTile(f, () =>
							setProofFiles((prev) => prev.filter((x) => x.key !== f.key))
						)
					)}
					<Upload beforeUpload={handlePaymentProof} showUploadList={false} multiple>
						<AddTile>
							<PlusOutlined />
							Add Image
						</AddTile>
					</Upload>
				</Tiles>
			</Field>

			<Footer>
				<Button
					style={{ height: 40, padding: '0 24px', borderRadius: 6 }}
					onClick={onClose}
				>
					Cancel
				</Button>
				<PrimaryButton onClick={handleSave} loading={saving} disabled={uploading}>
					Confirm
				</PrimaryButton>
			</Footer>
		</StyledModal>
	);
}

MarkPaymentModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	invoice: PropTypes.object,
	payments: PropTypes.arrayOf(PropTypes.object),
	saving: PropTypes.bool,
	onSave: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired,
};

MarkPaymentModal.defaultProps = {
	invoice: {},
	payments: [],
	saving: false,
};

export default MarkPaymentModal;
